<template>
  <container>
    <h1 style="font-size:25px;text-align:center;position:relative;">
      System Settings
      <button 
        class="refresh-button" 
        @click="loadSystemStats" 
        :disabled="loading"
        title="Refresh"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 4v6h6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M23 20v-6h-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </h1>

    <!-- Loading State -->
    <div v-if="loading" style="text-align:center; padding: 2rem;">
      <p>Loading system information...</p>
    </div>

    <!-- Error State -->
    <div v-if="error" style="text-align:center; padding: 2rem; color: red;">
      <p style="margin-bottom: 1rem;">{{ error }}</p>
      <button class="btn btn--primary" @click="loadSystemStats" style="margin-top: 0.5rem;">Retry</button>
    </div>

    <!-- System Stats -->
    <div v-if="!loading && !error && systemStats">
      <!-- CPU Information -->
      <div class="stats-section">
        <div class="section-header">
          <h2>CPU Information</h2>
          <span class="last-updated">Last updated: {{ formatTime(systemStats.timestamp) }}</span>
        </div>
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-label">CPU Usage</div>
            <div class="stat-value">{{ systemStats.cpu.usage.toFixed(2) }}%</div>
            <div class="stat-bar">
              <div 
                class="stat-bar-fill" 
                :style="{ width: systemStats.cpu.usage + '%', backgroundColor: getUsageColor(systemStats.cpu.usage) }"
              ></div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-label">CPU Model</div>
            <div class="stat-value-small">{{ systemStats.cpu.model }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">CPU Cores</div>
            <div class="stat-value-small">{{ systemStats.cpu.cores }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Load Average (1min)</div>
            <div class="stat-value-small">{{ systemStats.cpu.loadAverage.avg1min.toFixed(2) }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Load Average (5min)</div>
            <div class="stat-value-small">{{ systemStats.cpu.loadAverage.avg5min.toFixed(2) }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Load Average (15min)</div>
            <div class="stat-value-small">{{ systemStats.cpu.loadAverage.avg15min.toFixed(2) }}</div>
          </div>
        </div>
      </div>

      <!-- Memory Information -->
      <div class="stats-section">
        <div class="section-header">
          <h2>Memory Information</h2>
        </div>
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-label">Memory Usage</div>
            <div class="stat-value">{{ systemStats.memory.usagePercentage.toFixed(2) }}%</div>
            <div class="stat-bar">
              <div 
                class="stat-bar-fill" 
                :style="{ width: systemStats.memory.usagePercentage + '%', backgroundColor: getUsageColor(systemStats.memory.usagePercentage) }"
              ></div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Total Memory</div>
            <div class="stat-value-small">{{ systemStats.memory.totalFormatted }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Used Memory</div>
            <div class="stat-value-small">{{ systemStats.memory.usedFormatted }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Free Memory</div>
            <div class="stat-value-small">{{ systemStats.memory.freeFormatted }}</div>
          </div>
        </div>
      </div>

      <!-- PM2 Processes -->
      <div class="stats-section">
        <div class="section-header">
          <h2>PM2 Processes</h2>
          <button 
            class="btn btn--primary" 
            @click="confirmRestartPM2"
            :disabled="restarting"
            style="margin-left: auto;"
          >
            {{ restarting ? 'Restarting...' : 'Restart PM2' }}
          </button>
        </div>
        
        <div v-if="systemStats.pm2 && systemStats.pm2.length > 0">
          <table class="pm2-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>CPU %</th>
                <th>Memory</th>
                <th>Uptime</th>
                <th>Restarts</th>
                <th>PID</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="process in systemStats.pm2" :key="process.pmId || process.pid">
                <td>{{ process.name }}</td>
                <td>
                  <span :class="'status-badge status-' + process.status.toLowerCase()">
                    {{ process.status }}
                  </span>
                </td>
                <td>{{ process.cpu.toFixed(2) }}%</td>
                <td>{{ process.memoryFormatted }}</td>
                <td>{{ process.uptimeFormatted }}</td>
                <td>{{ process.restarts }}</td>
                <td>{{ process.pid || 'N/A' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else style="text-align:center; padding: 2rem; color: #666;">
          <p>No PM2 processes found</p>
        </div>
      </div>

      <!-- Auto-refresh indicator -->
      <div style="text-align:center; margin-top: 2rem; color: #666; font-size: 12px;">
        <p>Auto-refreshing every {{ refreshInterval / 1000 }} seconds</p>
        <button class="btn btn--secondary" @click="toggleAutoRefresh" style="margin-top: 0.5rem;">
          {{ autoRefreshEnabled ? 'Disable' : 'Enable' }} Auto-refresh
        </button>
      </div>
    </div>
  </container>
</template>

<script>
import Vue from 'vue'
import Container from "@/components/Container"
import SystemSettingsService from "@/services/systemSettings"
import VuejsDialog from 'vuejs-dialog'

Vue.use(VuejsDialog)

let dialogOptions = {
  html: false,
  loader: false,
  reverse: false,
  cancelText: 'Cancel',
  okText: 'Restart',
  animation: 'zoom',
  type: 'basic'
}

export default {
  name: "system-settings",
  data() {
    return {
      loading: true,
      error: null,
      systemStats: null,
      refreshInterval: 30000, // 30 seconds
      autoRefreshEnabled: true,
      refreshTimer: null,
      restarting: false
    }
  },
  methods: {
    async loadSystemStats() {
      this.loading = true
      this.error = null
      
      try {
        const response = await SystemSettingsService.getSystemStats()
        
        if (response.data && response.data.success) {
          this.systemStats = response.data.data
          this.error = null
        } else {
          this.error = 'Failed to load system statistics'
        }
      } catch (err) {
        console.error('Error loading system stats:', err)
        this.error = (err.response && err.response.data && err.response.data.message) || err.message || 'Failed to load system statistics'
      } finally {
        this.loading = false
      }
    },

    confirmRestartPM2() {
      this.$dialog.confirm(
        "Are you sure you want to restart all PM2 processes? This will temporarily interrupt service.",
        dialogOptions
      ).then((dialog) => {
        this.restartPM2()
      }).catch(() => {
        // User cancelled
      })
    },

    async restartPM2() {
      this.restarting = true
      
      try {
        const response = await SystemSettingsService.restartPM2()
        
        if (response.data && response.data.success) {
          this.$dialog.alert('PM2 processes restarted successfully', {
            okText: 'OK'
          })
          
          // Reload stats after a short delay
          setTimeout(() => {
            this.loadSystemStats()
          }, 2000)
        } else {
          this.$dialog.alert('Failed to restart PM2: ' + (response.data && response.data.message ? response.data.message : 'Unknown error'), {
            okText: 'OK'
          })
        }
      } catch (err) {
        console.error('Error restarting PM2:', err)
        const errorMessage = (err.response && err.response.data && err.response.data.message) || err.message || 'Unknown error'
        this.$dialog.alert(
          'Failed to restart PM2: ' + errorMessage,
          { okText: 'OK' }
        )
      } finally {
        this.restarting = false
      }
    },

    toggleAutoRefresh() {
      this.autoRefreshEnabled = !this.autoRefreshEnabled
      
      if (this.autoRefreshEnabled) {
        this.startAutoRefresh()
      } else {
        this.stopAutoRefresh()
      }
    },

    startAutoRefresh() {
      this.stopAutoRefresh() // Clear any existing timer
      
      if (this.autoRefreshEnabled) {
        this.refreshTimer = setInterval(() => {
          this.loadSystemStats()
        }, this.refreshInterval)
      }
    },

    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },

    formatTime(timestamp) {
      if (!timestamp) return 'N/A'
      const date = new Date(timestamp)
      return date.toLocaleTimeString()
    },

    getUsageColor(usage) {
      if (usage >= 80) return '#ff5627' // Red
      if (usage >= 60) return '#ffa500' // Orange
      return '#4caf50' // Green
    }
  },
  created() {
    this.loadSystemStats()
    if (this.autoRefreshEnabled) {
      this.startAutoRefresh()
    }
  },
  beforeDestroy() {
    this.stopAutoRefresh()
  },
  components: {
    Container
  }
}
</script>

<style scoped>
.stats-section {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 1.5rem;
  margin: 20px 0;
  background: #fff;
  box-shadow: 0 0 10px rgba(0, 0, 0, .05);
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #eee;
}

.section-header h2 {
  font-size: 20px;
  margin: 0;
  color: #423799;
}

.last-updated {
  margin-left: auto;
  font-size: 12px;
  color: #666;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.stat-card {
  padding: 1rem;
  background: #f9f9f9;
  border-radius: 4px;
  border: 1px solid #eee;
}

.stat-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #423799;
  margin-bottom: 0.5rem;
}

.stat-value-small {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.stat-bar {
  width: 100%;
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 0.5rem;
}

.stat-bar-fill {
  height: 100%;
  transition: width 0.3s ease;
  border-radius: 4px;
}

.pm2-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
}

.pm2-table thead {
  background: #f5f5f5;
}

.pm2-table th {
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #ddd;
}

.pm2-table td {
  padding: 12px;
  border-bottom: 1px solid #eee;
}

.pm2-table tbody tr:hover {
  background: #f9f9f9;
}

.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-online {
  background: #4caf50;
  color: white;
}

.status-stopped {
  background: #f44336;
  color: white;
}

.status-restarting {
  background: #ff9800;
  color: white;
}

.status-unknown {
  background: #9e9e9e;
  color: white;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: background-color 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
}

.btn--primary {
  background: #423799;
  color: white;
}

.btn--primary:hover:not(:disabled) {
  background: #352c7a;
}

.btn--primary:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.btn--secondary {
  background: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
}

.btn--secondary:hover {
  background: #e0e0e0;
}

.refresh-button {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  transition: color 0.3s ease, transform 0.3s ease;
  border-radius: 4px;
}

.refresh-button:hover:not(:disabled) {
  color: #423799;
  background: #f5f5f5;
}

.refresh-button:active:not(:disabled) {
  transform: translateY(-50%) rotate(180deg);
}

.refresh-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.refresh-button svg {
  width: 20px;
  height: 20px;
}
</style>




// WEBPACK FOOTER //
// SystemSettings.vue?5dd84c22